import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

// PAR Meter with RGB outer ring - standalone version for use anywhere
function PARMeter({
  value,
  min = 0,
  max = 1500,
  optimalMin = 600,
  optimalMax = 1000,
  size = 120,
  rgb = { r: 185, g: 120, b: 45 },
  color = '#f59e0b',
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
  const centerX = size / 2;
  const centerY = size / 2;

  // Ring dimensions (scaled to size)
  const scale = size / 120;
  const outerRadius = size * 0.42;
  const innerRadius = size * 0.28;
  const outerStrokeWidth = 10 * scale;
  const innerStrokeWidth = 12 * scale;

  // Calculate RGB percentages for the ring segments
  const total = rgb.r + rgb.g + rgb.b;
  const rPercent = rgb.r / total;
  const gPercent = rgb.g / total;
  const bPercent = rgb.b / total;

  // Outer ring circumference
  const outerCircum = 2 * Math.PI * outerRadius;
  const filledLength = outerCircum * normalizedValue;

  // Inner ring circumference
  const innerCircum = 2 * Math.PI * innerRadius;
  const innerFilled = innerCircum * normalizedValue;

  // Calculate arc segments for RGB on outer ring
  const rLength = filledLength * rPercent;
  const gLength = filledLength * gPercent;
  const bLength = filledLength * bPercent;

  // Optimal range arc positions (for inner green arc)
  const optMinNorm = optimalMin !== undefined ? (optimalMin - min) / (max - min) : 0.4;
  const optMaxNorm = optimalMax !== undefined ? (optimalMax - min) / (max - min) : 0.67;
  const optimalStartAngle = -90 + (optMinNorm * 360);
  const optimalArcLength = innerCircum * (optMaxNorm - optMinNorm);

  // RGB optimal range bar positions (outer edge)
  const rgbOptRadius = outerRadius + outerStrokeWidth / 2 + 3 * scale;
  const rgbOpt = {
    r: { min: 150, max: 200 },
    g: { min: 100, max: 140 },
    b: { min: 35, max: 55 },
  };

  const calcOptimalArc = (optMin, optMax, maxVal) => {
    const minNorm = optMin / maxVal;
    const maxNorm = optMax / maxVal;
    return {
      startAngle: -90 + (minNorm * 360),
      length: 2 * Math.PI * rgbOptRadius * (maxNorm - minNorm),
    };
  };

  const rOptArc = calcOptimalArc(rgbOpt.r.min, rgbOpt.r.max, 250);
  const gOptArc = calcOptimalArc(rgbOpt.g.min, rgbOpt.g.max, 180);
  const bOptArc = calcOptimalArc(rgbOpt.b.min, rgbOpt.b.max, 100);

  // Generate unique filter ID
  const filterId = React.useMemo(() => `parGlow-${Math.random().toString(36).substr(2, 9)}`, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <filter id={filterId}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Outer RGB optimal range bars (on outer edge) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={rgbOptRadius}
          fill="none"
          stroke="#ef4444"
          strokeWidth={2 * scale}
          strokeDasharray={`${rOptArc.length} ${2 * Math.PI * rgbOptRadius - rOptArc.length}`}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${rOptArc.startAngle} ${centerX} ${centerY})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={rgbOptRadius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={2 * scale}
          strokeDasharray={`${gOptArc.length} ${2 * Math.PI * rgbOptRadius - gOptArc.length}`}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${gOptArc.startAngle} ${centerX} ${centerY})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={rgbOptRadius}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={2 * scale}
          strokeDasharray={`${bOptArc.length} ${2 * Math.PI * rgbOptRadius - bOptArc.length}`}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${bOptArc.startAngle} ${centerX} ${centerY})`}
        />

        {/* Outer RGB ring background */}
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={outerStrokeWidth}
        />

        {/* Outer RGB segments (Blue, Green, Red stacked) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={outerStrokeWidth}
          strokeDasharray={`${bLength} ${outerCircum - bLength}`}
          strokeDashoffset={outerCircum * 0.25}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={outerStrokeWidth}
          strokeDasharray={`${gLength} ${outerCircum - gLength}`}
          strokeDashoffset={outerCircum * 0.25 - bLength}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#ef4444"
          strokeWidth={outerStrokeWidth}
          strokeDasharray={`${rLength} ${outerCircum - rLength}`}
          strokeDashoffset={outerCircum * 0.25 - bLength - gLength}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
        />

        {/* Inner ring background */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={innerStrokeWidth}
        />

        {/* Inner optimal range arc (green) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius - innerStrokeWidth / 2 - 2 * scale}
          fill="none"
          stroke="#22c55e"
          strokeWidth={3 * scale}
          strokeDasharray={`${optimalArcLength} ${innerCircum - optimalArcLength}`}
          strokeLinecap="round"
          opacity={0.4}
          transform={`rotate(${optimalStartAngle} ${centerX} ${centerY})`}
        />

        {/* Inner user color ring (sensor value) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="none"
          stroke={color}
          strokeWidth={innerStrokeWidth}
          strokeDasharray={`${innerFilled} ${innerCircum - innerFilled}`}
          strokeDashoffset={innerCircum * 0.25}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
        />

        {/* Center text - PAR value */}
        <text
          x={centerX}
          y={centerY - 2 * scale}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={color}
          fontSize={14 * scale}
          fontWeight={700}
          fontFamily="Inter, sans-serif"
        >
          {value.toFixed(0)}
        </text>
        <text
          x={centerX}
          y={centerY + 12 * scale}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#9ca3af"
          fontSize={7 * scale}
          fontFamily="Inter, sans-serif"
        >
          PPFD
        </text>
      </svg>
    </Box>
  );
}

PARMeter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  size: PropTypes.number,
  rgb: PropTypes.shape({
    r: PropTypes.number,
    g: PropTypes.number,
    b: PropTypes.number,
  }),
  color: PropTypes.string,
};

export default PARMeter;
